import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Sai số cho phép giữa vị trí thật của target và `scroll-margin-top` của nó. */
const TOLERANCE_PX = 4;
/** Trần thời gian đuổi theo layout. Dài hơn thì người dùng đã đọc xong phần đầu trang. */
const MAX_MS = 2500;
const STEP_MS = 100;

/**
 * Cuộn tới `#fragment` của URL, và ĐUỔI THEO layout cho tới khi nó lắng.
 *
 * Vì sao trình duyệt tự làm không đủ — hai ca ĐO ĐƯỢC, cả hai đều là bug thật:
 *
 * 1. **Target chưa tồn tại lúc parse.** Trình duyệt tìm `#id` một lần, lúc parse HTML. Ở dev
 *    server (`vite dev`) HTML là vỏ rỗng — đo được: `curl :5173/vi/ | grep -c 'id="collab"'`
 *    ⇒ **0**, trong khi bản build ⇒ **1**. Không có target thì không cuộn, và trình duyệt
 *    KHÔNG thử lại khi React render nó ra sau.
 * 2. **Layout lệch SAU khi cuộn xong.** Trang dùng `content-visibility: auto`, nên chiều cao
 *    các section phía trên chỉ là số mồi (`--nx-cv-h`) cho tới khi chúng thật sự render. Đo
 *    trên bản build: bấm nav tới `#faq` từ `/vi/terms/` ⇒ cuộn xong ở `top=-50`, tức trôi
 *    **118px** khỏi mốc `scroll-mt-[68px]` và chui xuống dưới header dính. Càng xa đầu trang
 *    càng lệch: `#collab` đáp đúng `top=68`, `#faq` thì không.
 *
 * Vì sao TẮT `scroll-behavior: smooth` trong lúc đáp: `global.css:28` bật `smooth` cho `html`,
 * nên lượt cuộn tới fragment được ANIMATE. Nhưng target ở đây di chuyển trong lúc animation đang
 * chạy — các section phía trên còn đang render (`content-visibility: auto`) — nên nó không phải
 * một lượt cuộn mượt tới một đích cố định, nó là một lượt đuổi theo đích đang trôi: **khựng**.
 * Đo được: `#faq` đi qua 4 mốc trung gian trong ~3,5s trước khi đứng yên.
 *
 * ⇒ Trong lúc đáp thì đặt `html { scroll-behavior: auto }` (ghi đè inline), nhảy THẲNG, rồi
 * TRẢ LẠI giá trị cũ khi đã lắng. Nhờ vậy click anchor CÙNG TRANG vẫn mượt như thiết kế — hook
 * này chỉ chạy khi `useLocation().hash` đổi, tức khi điều hướng chéo trang, không phải khi trình
 * duyệt tự cuộn trong cùng một tài liệu.
 *
 * Vì sao dừng khi người dùng chạm vào: đuổi theo layout mà không nhả thì nó cướp quyền cuộn của
 * người đọc — cùng lớp với một cổng không có cách tắt. Bất kỳ `wheel`/`touchstart`/`keydown` nào
 * cũng huỷ ngay.
 */
export function useHashScroll(): void {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '') return;

    let stop = false;
    // Ghi đè inline thắng `@layer base` của `global.css`, và nhớ giá trị cũ để trả đúng thứ đã
    // có — không gán cứng `''`, vì có thể ai đó đã đặt inline từ trước.
    const root = document.documentElement;
    const prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    const restore = () => {
      root.style.scrollBehavior = prevBehavior;
    };

    const cancel = () => {
      stop = true;
      restore();
    };
    const opts = { passive: true, once: true } as const;
    window.addEventListener('wheel', cancel, opts);
    window.addEventListener('touchstart', cancel, opts);
    window.addEventListener('keydown', cancel, opts);

    let elapsed = 0;
    let timer: number | undefined;

    const tick = () => {
      if (stop) return;

      // `hash` đến từ URL nên có thể là selector không hợp lệ (`#https://…` nếu ai đó dựng href
      // sai). `querySelector` ném SyntaxError ở đó, và một handler cuộn KHÔNG được phép làm chết
      // cả trang vì một URL xấu.
      let target: Element | null;
      try {
        target = document.querySelector(hash);
      } catch {
        restore();
        return;
      }

      if (target !== null) {
        const top = target.getBoundingClientRect().top;
        const goal = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        if (Math.abs(top - goal) <= TOLERANCE_PX) {
          restore(); // đã đúng chỗ: thôi đuổi, và trả lại `smooth` cho click cùng trang
          return;
        }
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      elapsed += STEP_MS;
      if (elapsed < MAX_MS) timer = window.setTimeout(tick, STEP_MS);
      else restore(); // hết trần thời gian cũng phải trả, không thì trang mất `smooth` vĩnh viễn
    };

    timer = window.setTimeout(tick, 0);

    return () => {
      stop = true;
      restore();
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    };
  }, [hash]);
}
